import { useState, useEffect, useCallback } from 'react';
import {
  Clock,
  Loader2,
  AlertCircle,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
} from 'recharts';
import clsx from 'clsx';
import { periodDisplayName, type Period, type CloseTimeline, type EntityReadiness, type Bottleneck } from '@/types';
import { periodsApi, predictiveCloseApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Helpers -----------------------------------------------------------------

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function confidenceColor(confidence: number): { label: string; bg: string; text: string } {
  if (confidence >= 0.8) return { label: 'High', bg: 'bg-green-50', text: 'text-green-700' };
  if (confidence >= 0.5) return { label: 'Medium', bg: 'bg-amber-50', text: 'text-amber-700' };
  return { label: 'Low', bg: 'bg-red-50', text: 'text-red-700' };
}

function stageStatusDot(completed: boolean): string {
  return completed ? 'bg-green-500' : 'bg-gray-300';
}

// --- Main Page ---------------------------------------------------------------

export default function PredictiveClose() {
  const { addNotification } = useAppStore();

  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Period
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');

  // Data
  const [timeline, setTimeline] = useState<CloseTimeline | null>(null);
  const [readiness, setReadiness] = useState<EntityReadiness[]>([]);
  const [bottlenecks, setBottlenecks] = useState<Bottleneck[]>([]);

  // Load periods
  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const res = await periodsApi.list();
        setPeriods(res);
        const openPeriod = res.find((p) => p.status === 'open') || res[0];
        if (openPeriod) setSelectedPeriod(openPeriod.id);
      } catch {
        // silently fail
      }
    };
    loadPeriods();
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    if (!selectedPeriod) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const [timelineRes, readinessRes, bottlenecksRes] = await Promise.all([
        predictiveCloseApi.get(Number(selectedPeriod)).catch(() => null),
        predictiveCloseApi.readiness(Number(selectedPeriod)).catch(() => []),
        predictiveCloseApi.bottlenecks(Number(selectedPeriod)).catch(() => []),
      ]);
      setTimeline(timelineRes);
      setReadiness(Array.isArray(readinessRes) ? readinessRes : []);
      setBottlenecks(Array.isArray(bottlenecksRes) ? bottlenecksRes : []);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load predictive close data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    if (selectedPeriod) loadData();
  }, [loadData, selectedPeriod]);

  // Refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadData();
      addNotification({ type: 'success', title: 'Prediction Updated', message: 'Close prediction has been refreshed.' });
    } finally {
      setRefreshing(false);
    }
  };

  // Timeline bar chart data
  const timelineBarData = timeline?.stages_timeline?.map((s) => ({
    stage: s.stage,
    date: formatDate(s.estimated_date),
    daysFromNow: Math.max(0, Math.ceil(
      (new Date(s.estimated_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24),
    )),
  })) || [];

  // Historical data placeholder (in real app would come from API)
  const historicalData = Array.from({ length: 6 }, (_, i) => ({
    period: `Q${(i % 4) + 1} ${2024 + Math.floor(i / 4)}`,
    days: Math.floor(Math.random() * 10) + 8,
  }));

  // Confidence indicator
  const conf = timeline ? confidenceColor(timeline.confidence) : null;

  // Skeleton loading
  if (isLoading && !timeline && readiness.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <Clock className="w-5 h-5 text-purple-700" />
          </div>
          <div>
            <div className="skeleton w-56 h-7 mb-1" />
            <div className="skeleton w-80 h-4" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card p-6 animate-pulse"><div className="skeleton w-full h-48" /></div>
          <div className="card p-6 animate-pulse"><div className="skeleton w-full h-48" /></div>
        </div>
        <div className="card p-6 animate-pulse">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="skeleton w-full h-10" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <Clock className="w-5 h-5 text-purple-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Predictive Close</h1>
            <p className="text-sm text-gray-500 mt-0.5">AI-powered close timeline prediction and bottleneck analysis</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="select-field text-sm w-40"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            {periods.length === 0 && <option value="">No periods</option>}
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{periodDisplayName(p)}</option>
            ))}
          </select>
          <button
            onClick={handleRefresh}
            disabled={refreshing || !selectedPeriod}
            className="btn-primary"
          >
            {refreshing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh Prediction
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadData} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* No data */}
      {!timeline && readiness.length === 0 && !error ? (
        <div className="card py-16 text-center">
          <Clock className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">No prediction data available</h3>
          <p className="text-sm text-gray-500 mt-1">
            Click "Refresh Prediction" to generate close timeline predictions.
          </p>
        </div>
      ) : (
        <>
          {/* Prediction Summary */}
          {timeline && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="card p-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Predicted End Date</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{formatDate(timeline.predicted_end_date)}</p>
                <p className="text-xs text-gray-400 mt-1">Started: {formatDate(timeline.start_date)}</p>
              </div>
              <div className="card p-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Confidence</p>
                <div className="flex items-center gap-3 mt-1">
                  <p className="text-2xl font-bold text-gray-900 font-mono">
                    {Math.round(timeline.confidence * 100)}%
                  </p>
                  {conf && (
                    <span className={clsx('badge', conf.bg, conf.text)}>
                      {conf.label}
                    </span>
                  )}
                </div>
              </div>
              <div className="card p-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Bottlenecks</p>
                <p className={clsx(
                  'text-2xl font-bold mt-1 font-mono',
                  bottlenecks.length > 0 ? 'text-red-700' : 'text-green-700',
                )}>
                  {bottlenecks.length}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {bottlenecks.length > 0 ? 'Blocking items detected' : 'No bottlenecks found'}
                </p>
              </div>
            </div>
          )}

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Stage Timeline */}
            {timelineBarData.length > 0 && (
              <div className="card p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Stage Timeline (Days Remaining)</h2>
                <ResponsiveContainer width="100%" height={Math.max(200, timelineBarData.length * 40)}>
                  <BarChart data={timelineBarData} layout="vertical" barSize={24}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                    <XAxis
                      type="number"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      label={{ value: 'Days', position: 'bottom', fontSize: 11, fill: '#94a3b8' }}
                    />
                    <YAxis
                      type="category"
                      dataKey="stage"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#374151' }}
                      width={130}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '13px',
                      }}
                      formatter={(value: number, _name: string, props: any) => [
                        `${value} days (${props.payload.date})`,
                        'Estimated',
                      ]}
                    />
                    <Bar dataKey="daysFromNow" name="Days" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Historical Close Times */}
            <div className="card p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Historical Close Times</h2>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={historicalData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis
                    dataKey="period"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#64748b' }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    label={{ value: 'Days', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#94a3b8' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '13px',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="days"
                    name="Close Days"
                    stroke="#7c3aed"
                    strokeWidth={2}
                    dot={{ r: 4, fill: '#7c3aed' }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Entity Readiness Table */}
          {readiness.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
                <h2 className="text-base font-semibold text-gray-900">Entity Readiness</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="table-header">Entity</th>
                      <th className="table-header">Stage Indicators</th>
                      <th className="table-header text-right w-28">Completion</th>
                      <th className="table-header w-36">Est. Completion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {readiness.map((entity) => {
                      const stages = Object.entries(entity.stages);
                      return (
                        <tr
                          key={entity.entity_id}
                          className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors"
                        >
                          <td className="table-cell text-sm font-medium text-gray-800">
                            {entity.entity_name}
                          </td>
                          <td className="table-cell">
                            <div className="flex items-center gap-1.5">
                              {stages.map(([stage, done]) => (
                                <div
                                  key={stage}
                                  title={`${stage}: ${done ? 'Complete' : 'Pending'}`}
                                  className={clsx(
                                    'w-3 h-3 rounded-full',
                                    stageStatusDot(done),
                                  )}
                                />
                              ))}
                            </div>
                          </td>
                          <td className="table-cell text-right">
                            <div className="flex items-center gap-2 justify-end">
                              <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className={clsx(
                                    'h-full rounded-full',
                                    entity.completion_pct >= 100 ? 'bg-green-500' :
                                    entity.completion_pct >= 50 ? 'bg-blue-500' : 'bg-amber-500',
                                  )}
                                  style={{ width: `${Math.min(100, entity.completion_pct)}%` }}
                                />
                              </div>
                              <span className="text-xs font-mono text-gray-700 w-10 text-right">
                                {entity.completion_pct}%
                              </span>
                            </div>
                          </td>
                          <td className="table-cell text-sm text-gray-600">
                            {formatDate(entity.estimated_completion)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Bottleneck Cards */}
          {bottlenecks.length > 0 && (
            <div>
              <h2 className="text-base font-semibold text-gray-900 mb-4">Bottlenecks</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bottlenecks.map((b, idx) => (
                  <div key={idx} className="card p-5 border-l-4 border-l-red-500">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-gray-900">{b.entity_name}</p>
                          <span className="badge bg-red-50 text-red-700 text-xs">
                            +{b.impact_days} days
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mb-1">
                          Stage: <span className="font-medium text-gray-700">{b.stage}</span>
                        </p>
                        <p className="text-sm text-gray-700">{b.reason}</p>
                        {b.suggestion && (
                          <div className="mt-2 p-2 rounded bg-blue-50 border border-blue-200">
                            <p className="text-xs text-blue-700">
                              <span className="font-semibold">Suggestion:</span> {b.suggestion}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
